/**
 * Severity ladder: syslog (RFC 5424) plus a sub-debug `Trace` (super-noisy,
 * off by default) and a threshold-only `Silent` (never a valid entry level).
 */
export enum LogLevel {
    Trace = 'trace',
    Debug = 'debug',
    Info = 'info',
    Notice = 'notice',
    Warn = 'warn',
    Error = 'error',
    Critical = 'critical',
    Alert = 'alert',
    Emergency = 'emergency',
    Silent = 'silent',
}

/** A level that can appear on an emitted entry (everything except Silent). */
export type EmittableLogLevel = Exclude<LogLevel, LogLevel.Silent>;

/** Structured metadata attached to a log entry — for machines, not humans. */
export type LogMeta = Record<string, unknown>;

/** One emitted entry: `meta` is chain + call-site (call-site wins); `context` is the ambient layers. */
export interface LogEntry {
    context: LogMeta;
    level: EmittableLogLevel;
    /** Human-readable message, self-sufficient without expanding `meta`. */
    message: string;
    meta: LogMeta;
    /** Dotted logger name (empty string for the root logger). */
    name: string;
    timestamp: number;
}

/** An output for entries (console, Datadog, …); destinations are isolated from each other. */
export interface LogDestination {
    /** Minimum level this destination accepts, on top of the logger threshold. */
    readonly minLevel?: LogLevel;
    readonly name: string;
    /**
     * Replay buffered pre-configuration entries into this destination on
     * attach. Console-style destinations stay unset — replay would double-print.
     */
    readonly receivesReplay?: boolean;
    /**
     * Whether the runtime filter overlay governs visibility. Console opts in;
     * shipping destinations must not, so a whitelist never floods a remote index.
     */
    readonly respectsOverlay?: boolean;
    write(entry: LogEntry): void;
}

/** Values contributed by a context layer. */
export type LogContextValues = LogMeta;

/** Lazy layer resolver, invoked per entry; a throw or null contributes nothing. */
export type LogContextResolver = () => LogContextValues | null;

/** A context layer is either a static value set or a lazy resolver. */
export type LogContextLayerInput = LogContextResolver | LogContextValues;

/** Removes a previously registered context layer, if still current. */
export type Disposer = () => void;

/** Options accepted when spawning a child logger. */
export interface ChildLoggerOptions {
    /** Metadata injected into every entry from this logger and its children. */
    meta?: LogMeta;
    /** Threshold override for this subtree (e.g. `http` runs at `Debug` so request logging is independent of the global level). */
    minLevel?: LogLevel;
}

/** Runtime configuration applied to the root logger by the composition root. */
export interface LoggerConfiguration {
    /** Replacement destination set (replaces, never appends). */
    destinations: readonly LogDestination[];
    /** Global minimum level for emission. */
    level: LogLevel;
    /** Persistence backend for the runtime filter overlay, if available. */
    overlayStorage?: OverlayStorage;
}

/** Platform persistence for overlay settings; the logging module never touches browser globals. */
export interface OverlayStorage {
    clear(): void;
    /** Load persisted state; null when absent, expired, or unreadable. */
    load(): null | PersistedOverlayState;
    save(state: PersistedOverlayState): void;
}

/** Serializable snapshot of the runtime filter overlay. */
export interface PersistedOverlayState {
    minLevel?: LogLevel;
    rules: readonly PersistedOverlayRule[];
}

/** Serializable form of a single overlay rule. */
export interface PersistedOverlayRule {
    /** Whether matching entries are shown (`white`) or hidden (`black`). */
    list: 'black' | 'white';
    /** Case-insensitive message substring, for message rules. */
    message?: string;
    /** Property predicates, for metadata rules. */
    predicates?: MetaPredicates;
}

/** Predicates keyed by property name, e.g. `{ someProperty: { eq: 12 } }`. */
export type MetaPredicates = Record<string, MetaPredicateOperators>;

/** Supported predicate operators for one property. */
export interface MetaPredicateOperators {
    /** Matches when the property strictly equals this value. */
    eq?: unknown;
}

/** The identity a logger node presents to the engine when dispatching. */
export interface LoggerNodeDescriptor {
    readonly chainMeta: LogMeta;
    readonly minLevel?: LogLevel;
    readonly name: string;
}

/** The logging surface shared by the root logger and all children. */
export interface Logger {
    readonly name: string;
    /** Spawn a named child inheriting this logger's metadata. */
    child(name: string, options?: ChildLoggerOptions): Logger;
    alert(message: string, meta?: LogMeta): void;
    critical(message: string, meta?: LogMeta): void;
    debug(message: string, meta?: LogMeta): void;
    emergency(message: string, meta?: LogMeta): void;
    error(message: string, meta?: LogMeta): void;
    info(message: string, meta?: LogMeta): void;
    notice(message: string, meta?: LogMeta): void;
    trace(message: string, meta?: LogMeta): void;
    warn(message: string, meta?: LogMeta): void;
}
